/**
 * Auth API Client (cookie-based).
 *
 * The frontend used to talk directly to Cognito and stash access /
 * refresh tokens in localStorage. That made any same-origin XSS into
 * a one-line account takeover (`JSON.parse(localStorage.getItem('fias_auth'))`).
 * This file now delegates every auth operation to our own backend at
 * `/v1/auth/*`, which sets httpOnly Secure cookies that JavaScript can
 * never read. Tokens never enter JS storage of any kind.
 *
 * Other callers of this module did not need to change — the public
 * methods (signIn / signUp / signOut / getCurrentUser / etc.) keep the
 * same shapes; only the implementations switched from "POST to Cognito,
 * write to localStorage" to "POST to our backend, let it set cookies".
 *
 * Two methods hand a string credential back to callers:
 *
 * - `getWebSocketTicket()` — mints a single-use 30s DynamoDB ticket via
 *   `POST /v1/auth/ws-ticket`. WebSocket / SSE handshakes can't carry
 *   cross-origin cookies reliably, so the ticket is what we put on the
 *   `?ticket=` query param. Single-use means a leaked URL is useless
 *   after the first handshake completes.
 *
 * - `getPluginBridgeToken(archeId)` — mints a capability JWT scoped to a
 *   single arche (audience=plugin-bridge, 5min TTL). The only credential
 *   the /v1/plugins/bridge/:archId route accepts; rejected at every
 *   other authenticated route. A leaked token grants ≤5min of access to
 *   one arche's bridge handler, never full-account authority. Cached
 *   per-arche in memory (with a 30s refresh margin) and invalidated on
 *   logout via `invalidatePluginBridgeToken(archeId)` / wholesale on
 *   `markSessionInactive()`.
 */

import { API_BASE } from './api';
import { setCsrfToken, getCsrfToken, attachCsrfHeader } from './csrf';
import {
  refreshAuthSession,
  markSessionEstablished,
  clearSessionEstablished,
  markSessionHint,
  clearSessionHint,
  hasSessionHint,
} from './auth-refresh';

// =============================================================================
// Dev mode
// =============================================================================
//
// VITE_DEV_MODE is honored only on hostnames that unambiguously identify a
// developer environment so a leaked build flag in staging/prod can never
// enable dev login. The backend pairs this with its own dev-mode gate
// (Cognito not configured + explicit opt-in), so even if the gate fails
// open here, the backend rejects dev tokens in any real env.
//
// Allowed hostnames:
//   - localhost / 127.0.0.1 (local WSL, Docker, native dev)
//   - *.app.github.dev      (GitHub Codespaces forwarded URLs)

const isDevHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.app.github.dev'));

if (import.meta.env.VITE_DEV_MODE === 'true' && !isDevHost) {
  console.error(
    '[Auth] SECURITY: VITE_DEV_MODE is set but hostname is not a recognized dev host. ' +
      'DEV_MODE has been disabled. This should never happen in production builds.',
  );
}

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true' && isDevHost;

const DEV_USER_STORAGE_KEY = 'fias_dev_user_index';

/**
 * Role assignments for dev users (mirrors the backend's
 * services/jwt-verifier.ts). Used purely so the dev-user picker UI can
 * preview which roles a given dev user holds — the source of truth at
 * runtime is the backend's `getDevUserRoles`.
 */
function getDevUserRoles(num: number): string[] {
  switch (num) {
    case 1:
      return ['user', 'admin', 'contributor', 'beta_tester', 'litcraft_admin'];
    case 2:
      return ['user', 'contributor', 'beta_tester'];
    case 3:
      return ['user', 'beta_tester'];
    case 4:
      return ['user', 'contributor'];
    default:
      return ['user'];
  }
}

const DEV_USERS: AuthUser[] = Array.from({ length: 10 }, (_, i) => ({
  userId: `dev-user-${String(i + 1).padStart(3, '0')}`,
  username: `devuser${i + 1}`,
  email: i === 0 ? 'dev@localhost' : `dev${i + 1}@localhost`,
  roles: getDevUserRoles(i + 1),
  emailVerified: true,
}));

function getSelectedDevUserIndex(): number {
  if (!DEV_MODE) return 0;
  try {
    const stored = localStorage.getItem(DEV_USER_STORAGE_KEY);
    if (stored !== null) {
      const index = parseInt(stored, 10);
      if (index >= 0 && index < DEV_USERS.length) return index;
    }
  } catch {
    // Ignore storage errors
  }
  return 0;
}

function setSelectedDevUserIndex(index: number): void {
  localStorage.setItem(DEV_USER_STORAGE_KEY, String(index));
}

// =============================================================================
// Public types (shape preserved across the migration)
// =============================================================================

export interface AuthUser {
  userId: string;
  username: string;
  email: string;
  roles: string[];
  emailVerified: boolean;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * Thrown by `authFetch` on a non-2xx response. Carries the HTTP status
 * so callers can distinguish auth failures (401) from service failures
 * (5xx) — important because any non-OK response on `/auth/ws-ticket`
 * used to be funneled through the refresh-and-retry path, which then
 * surfaced unrelated outages as "session expired" to the user.
 */
class AuthFetchError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(message: string, status: number, code: string | undefined) {
    super(message);
    this.name = 'AuthFetchError';
    this.status = status;
    this.code = code;
  }
}

/**
 * In-memory "we have a session" flag. Several streaming hooks call
 * `getIdToken()` synchronously as a gate to decide whether to attempt
 * a WebSocket connection at all (the actual ticket used for the
 * handshake is fetched async via `getWebSocketTicket`). Under cookie
 * auth we can't read the session token from JS, so we track presence
 * here and return a stable non-null marker when set. The marker is
 * never sent over the wire — it's purely a falsy/truthy signal.
 */
const SESSION_PRESENT_MARKER = 'fias-cookie-session';
let sessionPresent = false;

// In-memory cache for plugin bridge capability tokens.
//
// The server issues a capability JWT scoped to a single archeId with a
// 5-minute TTL. We cache per-arche so distinct plugins don't share a
// token (they can't — each is scoped to its own arche). The cache
// expires the entry 30s before the server's TTL so the in-flight
// bridge call doesn't race the expiry.
//
// Invalidated wholesale on logout via `markSessionInactive` so a
// freshly-logged-out plugin doesn't get its old token back.
const PLUGIN_BRIDGE_REFRESH_MARGIN_MS = 30 * 1000;
const cachedBridgeTokens = new Map<string, { token: string; expiresAt: number }>();

function markSessionActive(): void {
  sessionPresent = true;
  markSessionEstablished();
  // Persist a reload-durable hint so the next cold boot knows to attempt
  // refresh-and-retry (see getCurrentUser). Fires on login and every
  // confirmed /auth/me success.
  markSessionHint();
}

function markSessionInactive(): void {
  sessionPresent = false;
  cachedBridgeTokens.clear();
  clearSessionEstablished();
  clearSessionHint();
}

// =============================================================================
// Fetch wrapper
// =============================================================================

/**
 * Performs an auth-API request with cookies attached and the CSRF
 * token (if known) echoed in the X-CSRF-Token header. Throws an Error
 * with the user-facing message from the backend's `error.message` on
 * non-2xx responses.
 */
async function authFetch<T>(
  path: string,
  init: { method?: string; body?: Record<string, unknown> } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  attachCsrfHeader(method, headers);

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  // Capture CSRF token issued by login / refresh / dev-login responses
  // so subsequent state-changing requests can include it in the header.
  if (response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    const body = data as { csrfToken?: string } & Record<string, unknown>;
    if (typeof body.csrfToken === 'string') {
      setCsrfToken(body.csrfToken);
    }
    return body as T;
  }

  let message = response.statusText;
  let code: string | undefined;
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body.error?.message) message = body.error.message;
    if (body.error?.code) code = body.error.code;
  } catch {
    // Non-JSON error body — fall back to statusText
  }
  throw new AuthFetchError(message, response.status, code);
}

// =============================================================================
// Auth API
// =============================================================================

class AuthApiClient {
  // -------------------------------------------------------------------------
  // Sign-up
  // -------------------------------------------------------------------------

  async signUp(
    email: string,
    password: string,
    inviteCode: string,
    username: string,
  ): Promise<{ requiresConfirmation: boolean }> {
    return authFetch<{ requiresConfirmation: boolean }>('/auth/signup', {
      method: 'POST',
      body: { email: email.toLowerCase(), password, inviteCode, username },
    });
  }

  async confirmSignUp(
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ): Promise<void> {
    await authFetch('/auth/confirm-signup', {
      method: 'POST',
      body: {
        email: email.toLowerCase(),
        code,
        ...(inviteCode ? { inviteCode } : {}),
        ...(username ? { username } : {}),
      },
    });
  }

  async resendConfirmationCode(email: string): Promise<void> {
    await authFetch('/auth/resend-confirmation', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  // -------------------------------------------------------------------------
  // Sign-in / sign-out
  // -------------------------------------------------------------------------

  async signIn(email: string, password: string): Promise<{ user: AuthUser }> {
    if (DEV_MODE) {
      const devIndex = getSelectedDevUserIndex();
      console.warn(`[DEV_MODE] Bypassing Cognito — using ${DEV_USERS[devIndex].email}`);
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: devIndex + 1 },
      });
      markSessionActive();
      return { user: DEV_USERS[devIndex] };
    }

    await authFetch('/auth/login', {
      method: 'POST',
      body: { email: email.toLowerCase(), password },
    });
    markSessionActive();
    // The cookie is set by the login response. Hydrate the user
    // profile via /auth/me so callers get back the same shape they
    // used to receive from the Cognito ID-token decode.
    const me = await this.getCurrentUser();
    if (!me) {
      throw new Error('Login succeeded but /auth/me returned no user');
    }
    return { user: me };
  }

  async signOut(): Promise<void> {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Logout is best-effort — cookies may be cleared by the server
      // even if the response shape was unexpected.
    }
    setCsrfToken(null);
    markSessionInactive();
  }

  // -------------------------------------------------------------------------
  // Session lookup
  // -------------------------------------------------------------------------

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const result = await authFetch<{ user: AuthUser }>('/auth/me');
      markSessionActive();
      return result.user;
    } catch (err) {
      // The `fias_session` cookie only lives ~3h, but the `fias_refresh`
      // cookie lives 30 days. The overwhelmingly common case for a 401 here
      // is a returning user whose short session cookie lapsed while they were
      // away (overnight, a long meeting) — the refresh token can silently
      // mint a new session. Without this recovery, every cold app load after
      // ~3h forced a needless re-login even though the user is still within
      // their 30-day window. That was the "we have to log in too often" bug:
      // the proactive refresh timer in AuthContext is gated on `user` already
      // being set, so it never ran on boot, and this was the only session
      // probe at startup.
      //
      // So on a 401, attempt one refresh-and-retry before giving up — but
      // only when this browser plausibly holds a refresh token, so anonymous
      // visitors hitting an auth-gated probe don't pay an extra /auth/refresh
      // round-trip.
      //
      // The gate signal MUST be one the frontend can actually read on a cold
      // load. The app deploys cross-origin (frontend `platform.fias.io`, API
      // `api.platform.fias.io`), so the `fias_csrf` cookie is host-only for
      // the API origin and is NOT in the frontend's `document.cookie` on a
      // fresh page load — and the in-memory csrf cache is empty until the
      // first login/refresh response. That is precisely why the earlier
      // `getCsrfToken()`-only gate never fired in production: it was always
      // null here, so the recovery this block exists for was dead code, and
      // every cold load after the ~3h session lapsed forced a re-login even
      // though the 30-day refresh cookie was valid. So gate primarily on the
      // reload-durable, first-party `fias_session_hint` (localStorage, set on
      // login/refresh/`/auth/me` success); keep `getCsrfToken()` as a
      // secondary signal for same-origin/dev where the cookie IS readable.
      const status = err instanceof AuthFetchError ? err.status : undefined;
      if (status === 401 && (hasSessionHint() || getCsrfToken() !== null)) {
        const refreshed = await refreshAuthSession();
        if (refreshed) {
          try {
            const retry = await authFetch<{ user: AuthUser }>('/auth/me');
            markSessionActive();
            return retry.user;
          } catch {
            // Refresh succeeded but /auth/me still failed — fall through to
            // the logged-out path below.
          }
        }
      }
      // 401 with no recoverable session, or any transient error → logged out.
      markSessionInactive();
      return null;
    }
  }

  /**
   * Refreshes the session cookie by calling /auth/refresh. The backend
   * uses the (httpOnly) refresh cookie to obtain a new access token and
   * sets a fresh CSRF cookie. Returns true on success, false if the
   * refresh failed and the user must log in again.
   *
   * Delegates to the shared `refreshAuthSession` helper so this call
   * coalesces with any concurrent refreshes triggered by the api.ts
   * 401 interceptor or the proactive AuthContext timer — the backend
   * never sees a thundering herd of refresh requests.
   */
  async refreshSession(): Promise<boolean> {
    return refreshAuthSession();
  }

  // -------------------------------------------------------------------------
  // Password reset / change
  // -------------------------------------------------------------------------

  async forgotPassword(email: string): Promise<void> {
    await authFetch('/auth/forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await authFetch('/auth/confirm-forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase(), code, newPassword },
    });
  }

  /**
   * Change the password for the currently signed-in user. Backend
   * revokes all sessions (calls Cognito GlobalSignOut) and clears
   * cookies, so the caller is logged out on success.
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await authFetch('/auth/change-password', {
      method: 'POST',
      body: { oldPassword, newPassword },
    });
    setCsrfToken(null);
  }

  // -------------------------------------------------------------------------
  // Credentials for WebSocket handshakes and plugin iframe bridging
  //
  // Two separate concerns, two separate methods:
  //
  // - `getWebSocketTicket()` mints a single-use, 30s, DynamoDB-backed
  //   ticket via `POST /auth/ws-ticket`. Use as `?ticket=` query param
  //   on the WebSocket URL. Cannot be replayed; cannot be used against
  //   the REST API. This is the secure path — prefer it.
  //
  // - `getPluginBridgeToken(archeId)` mints a capability JWT scoped to
  //   a single arche (audience=plugin-bridge, 5min TTL). The token is
  //   accepted ONLY at /v1/plugins/bridge/:archId and explicitly
  //   rejected at every other authenticated route. A leaked token
  //   grants ≤5min of access to one arche's bridge handler — never
  //   full-account authority. Cached per-arche in memory.
  //
  // The deprecated `getValidIdToken()` / `getValidAccessToken()` are
  // shims preserved for the brief migration window only. They will be
  // removed in commit 2.
  // -------------------------------------------------------------------------

  async getWebSocketTicket(): Promise<string | null> {
    try {
      const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
      return result.ticket;
    } catch (err) {
      // Only 401 is an auth-related failure that the refresh-and-retry
      // path can recover from. Surface every other status (5xx, 429,
      // network failures) as a connection error so the streaming hook
      // shows CONNECTION_FAILED rather than masquerading the outage as
      // "your session has expired" — we used to swallow all errors here
      // and return null, which the WebSocket layer maps to the auth-
      // failure code path that fires the SessionExpiredListener modal.
      const status = err instanceof AuthFetchError ? err.status : undefined;
      if (status !== 401) throw err;
      const refreshed = await refreshAuthSession();
      if (!refreshed) return null;
      try {
        const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
        return result.ticket;
      } catch (retryErr) {
        const retryStatus = retryErr instanceof AuthFetchError ? retryErr.status : undefined;
        if (retryStatus !== 401) throw retryErr;
        return null;
      }
    }
  }

  /**
   * Mint (or read from cache) a plugin-bridge capability token scoped
   * to a specific arche. The token is the *only* credential the
   * /v1/plugins/bridge/:archId route accepts; it cannot be used at any
   * other authenticated route.
   *
   * `archeId` is required. Passing a different arche than the one the
   * plugin will call back on will produce a `BRIDGE_TOKEN_ARCHE_MISMATCH`
   * 403 at bridge time.
   */
  async getPluginBridgeToken(archeId: string): Promise<string | null> {
    if (typeof archeId !== 'string' || archeId.length === 0) {
      return null;
    }
    const now = Date.now();
    const cached = cachedBridgeTokens.get(archeId);
    if (cached && cached.expiresAt > now) {
      return cached.token;
    }
    try {
      const result = await authFetch<{ token: string; expiresIn: number }>(
        `/auth/plugin-bridge-token?archeId=${encodeURIComponent(archeId)}`,
      );
      // Server returns expiresIn in seconds. Refresh slightly before
      // the server's expiry so an in-flight bridge call doesn't race.
      const ttlMs = Math.max(0, result.expiresIn * 1000 - PLUGIN_BRIDGE_REFRESH_MARGIN_MS);
      cachedBridgeTokens.set(archeId, {
        token: result.token,
        expiresAt: now + ttlMs,
      });
      return result.token;
    } catch (err) {
      // Surface the REAL failure instead of swallowing it. Returning null
      // makes the caller send an empty token, which the bridge route rejects
      // with the generic "Plugin bridge requires token obtained from
      // /v1/auth/plugin-bridge-token" — masking the actual cause (e.g. a 500
      // from an unconfigured BRIDGE_TOKEN_SECRET, or a 4xx for a
      // non-plugin / unauthorized arche). Log the status/code loudly so the
      // root cause is visible; the null-on-failure contract is preserved.
      if (err instanceof AuthFetchError) {
        console.error(
          `[plugin-bridge-token] mint failed for ${archeId}: HTTP ${err.status}` +
            (err.code ? ` (${err.code})` : '') +
            ` — ${err.message}`,
        );
      } else {
        console.error(`[plugin-bridge-token] mint failed for ${archeId}:`, err);
      }
      return null;
    }
  }

  /**
   * Drop the cached bridge token for an arche. Called by the bridge
   * client when the server returns `BRIDGE_TOKEN_EXPIRED` so the next
   * `getPluginBridgeToken(archeId)` mints a fresh one.
   */
  invalidatePluginBridgeToken(archeId: string): void {
    cachedBridgeTokens.delete(archeId);
  }

  /**
   * Synchronous "do we have a session?" probe. Returns a stable
   * non-null marker string when the user is logged in, or null
   * otherwise. Used by streaming/WebSocket hooks as a render-time
   * gate; they call `getWebSocketTicket()` async to obtain the actual
   * ticket used in the WS handshake. The marker is never sent on the
   * wire — it's purely a truthy/falsy signal.
   */
  getIdToken(): string | null {
    return sessionPresent ? SESSION_PRESENT_MARKER : null;
  }

  getAccessToken(): string | null {
    return this.getIdToken();
  }

  // -------------------------------------------------------------------------
  // Dev mode helpers
  // -------------------------------------------------------------------------

  isDevMode(): boolean {
    return DEV_MODE;
  }

  getDevUsers(): AuthUser[] {
    return DEV_USERS;
  }

  /**
   * Switches to a different dev user. Re-runs /auth/dev-login so the
   * server-set cookies are updated to match the new dev user.
   */
  async switchDevUser(index: number): Promise<AuthUser | null> {
    if (!DEV_MODE) return null;
    if (index < 0 || index >= DEV_USERS.length) return null;
    setSelectedDevUserIndex(index);
    try {
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: index + 1 },
      });
      markSessionActive();
    } catch (err) {
      console.warn('[DEV_MODE] dev-login failed', err);
      return null;
    }
    return DEV_USERS[index];
  }
}

export const authApi = new AuthApiClient();
export default authApi;
